import { useCallback, useEffect, useState } from "react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import OperationsKPIs from "@/components/admin/OperationsKPIs";

const COLORS = ["#E6E6E6", "#8E9AA6", "#5b6975", "#3a4550"];

function fmtCurrency(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [revenueDaily, setRevenueDaily] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [active, setActive] = useState([]);
  const [err, setErr] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [o, d, c, b] = await Promise.all([
        api.get("/admin/metrics/overview"),
        api.get("/admin/metrics/revenue-by-day?days=30"),
        api.get("/admin/metrics/by-category?days=30"),
        api.get("/admin/bookings", { params: { status: "en_route", page_size: 50 } }),
      ]);
      setOverview(o.data);
      setRevenueDaily(d.data);
      setByCategory(c.data);
      const active1 = b.data.items || [];
      const more = await Promise.all(
        ["dispatched", "chauffeur_arrived", "passenger_loaded"].map((s) =>
          api.get("/admin/bookings", { params: { status: s, page_size: 50 } }).then((r) => r.data.items || [])
        )
      );
      setActive([...active1, ...more.flat()].sort((a, b) => a.pickup_datetime.localeCompare(b.pickup_datetime)));
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 15000);
    return () => clearInterval(i);
  }, [refresh]);

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div>
        <div className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-2">Overview</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tighter">Today at a glance</h1>
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}

      <OperationsKPIs />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Bookings Today" value={overview?.today?.bookings ?? "—"} testid="kpi-bookings-today" />
        <KPI label="Revenue Today" value={fmtCurrency(overview?.today?.revenue)} testid="kpi-revenue-today" />
        <KPI label="Active Drivers" value={overview?.active_drivers ?? "—"} testid="kpi-active-drivers" />
        <KPI label="Unassigned" value={overview?.unassigned_count ?? "—"} testid="kpi-unassigned" highlight={overview?.unassigned_count > 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-[#121214]/60 border border-white/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-4">Revenue · Last 30 Days</div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueDaily}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" stroke="#8E9AA6" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#8E9AA6" fontSize={10} />
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }} />
                <Line type="monotone" dataKey="revenue" stroke="#E6E6E6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#121214]/60 border border-white/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-4">By Category · 30d</div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byCategory} dataKey="revenue" nameKey="category" innerRadius={50} outerRadius={90} stroke="#0B0B0C">
                  {byCategory.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }} />
                <Legend wrapperStyle={{ fontSize: 10, color: "#8E9AA6" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-[#121214]/60 border border-white/10">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">Live</div>
            <div className="font-serif text-xl">Active Operations</div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">
            Auto-refresh every 15s · {active.length}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-active-ops-table">
            <thead className="text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6]">
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-3">Pickup</th>
                <th className="text-left px-5 py-3">Client</th>
                <th className="text-left px-5 py-3">Driver</th>
                <th className="text-left px-5 py-3">Vehicle</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {active.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-[#8E9AA6] text-xs uppercase tracking-[0.3em]">No active operations</td></tr>
              )}
              {active.map((b) => (
                <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3 font-light">{new Date(b.pickup_datetime).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                  <td className="px-5 py-3 font-light">{b.client_name}</td>
                  <td className="px-5 py-3 font-light">{b.driver_name || "—"}</td>
                  <td className="px-5 py-3 font-light">{b.vehicle_category.replace(/_/g, " ")}</td>
                  <td className="px-5 py-3"><span className="text-[10px] uppercase tracking-[0.25em] text-[#0B0B0C] bg-[#E6E6E6] px-2 py-0.5">{b.status.replace(/_/g, " ")}</span></td>
                  <td className="px-5 py-3 text-right tabular-nums">{fmtCurrency(b.total_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, testid, highlight }) {
  return (
    <div data-testid={testid} className={`bg-[#121214]/60 border p-5 ${highlight ? "border-amber-300/40" : "border-white/10"}`}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-2">{label}</div>
      <div className="font-serif text-3xl font-semibold">{value}</div>
    </div>
  );
}
